import React, { createContext, useContext, useState, useEffect } from 'react';
import { printSettingsAPI } from '../api/endpoints';

const BadgeSettingsContext = createContext();

export const useBadgeSettings = () => {
  const context = useContext(BadgeSettingsContext);
  if (!context) {
    throw new Error('useBadgeSettings must be used within a BadgeSettingsProvider');
  }
  return context;
};

export const BadgeSettingsProvider = ({ children }) => {
  const [defaultTemplate, setDefaultTemplate] = useState('classic');
  const [showQR, setShowQR] = useState(true);
  const [showLogo, setShowLogo] = useState(true);
  const [stickerSize, setStickerSize] = useState('small'); // 'tiny', 'small', 'normal', 'medium', 'large'
  const [defaultPrintMode, setDefaultPrintMode] = useState('sticker'); // 'badge' or 'sticker'
  const [stickerWidth, setStickerWidth] = useState(224); // Custom sticker width in pixels
  const [stickerHeight, setStickerHeight] = useState(176); // Custom sticker height in pixels
  const [useCustomStickerSize, setUseCustomStickerSize] = useState(false);
  const [printerType, setPrinterType] = useState('regular'); // 'regular' or 'thermal'
  const [thermalLabelSize, setThermalLabelSize] = useState('2x1'); // Thermal label size
  const [currentEventId, setCurrentEventId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // New sticker customization settings
  const [stickerNameFontSize, setStickerNameFontSize] = useState(14); // Font size for name in px
  const [stickerPositionFontSize, setStickerPositionFontSize] = useState(11); // Font size for position in px
  const [stickerOrgFontSize, setStickerOrgFontSize] = useState(11); // Font size for organization in px
  const [stickerLineHeight, setStickerLineHeight] = useState(1.2); // Line height multiplier
  const [stickerLogoSize, setStickerLogoSize] = useState(40); // Logo size in px
  const [stickerShowLogo, setStickerShowLogo] = useState(true); // Show logo on sticker
  const [stickerAutoScaleFont, setStickerAutoScaleFont] = useState(true); // Auto-scale font for long text
  const [qrPosition, setQrPosition] = useState('top'); // QR code position: 'top', 'bottom', 'left', 'right'
  // Font weights for text fields
  const [stickerNameFontWeight, setStickerNameFontWeight] = useState('bold'); // 'normal', 'medium', 'semibold', 'bold', 'extrabold'
  const [stickerPositionFontWeight, setStickerPositionFontWeight] = useState('semibold'); // Font weight for position
  const [stickerOrgFontWeight, setStickerOrgFontWeight] = useState('semibold'); // Font weight for organization
  const [stickerShowPosition, setStickerShowPosition] = useState(true); // Show position on sticker
  const [stickerShowOrganization, setStickerShowOrganization] = useState(true); // Show organization on sticker

  // Load settings from localStorage on mount
  useEffect(() => {
    const savedSettings = localStorage.getItem('badgeSettings');
    if (savedSettings) {
      try {
        const settings = JSON.parse(savedSettings);
        setDefaultTemplate(settings.defaultTemplate || 'standard');
        setShowQR(settings.showQR !== undefined ? settings.showQR : true);
        setShowLogo(settings.showLogo !== undefined ? settings.showLogo : true);
        setStickerSize(settings.stickerSize || 'small');
        setDefaultPrintMode(settings.defaultPrintMode || 'sticker');
        setStickerWidth(settings.stickerWidth || 224);
        setStickerHeight(settings.stickerHeight || 176);
        setUseCustomStickerSize(settings.useCustomStickerSize || false);
        setPrinterType(settings.printerType || 'regular');
        setThermalLabelSize(settings.thermalLabelSize || '2x1');
        setStickerNameFontSize(settings.stickerNameFontSize || 14);
        setStickerPositionFontSize(settings.stickerPositionFontSize || 11);
        setStickerOrgFontSize(settings.stickerOrgFontSize || 11);
        setStickerLineHeight(settings.stickerLineHeight || 1.2);
        setStickerLogoSize(settings.stickerLogoSize || 40);
        setStickerShowLogo(settings.stickerShowLogo !== undefined ? settings.stickerShowLogo : true);
        setStickerAutoScaleFont(settings.stickerAutoScaleFont !== undefined ? settings.stickerAutoScaleFont : true);
        setQrPosition(settings.qrPosition || 'top');
        setStickerNameFontWeight(settings.stickerNameFontWeight || 'bold');
        setStickerPositionFontWeight(settings.stickerPositionFontWeight || 'semibold');
        setStickerOrgFontWeight(settings.stickerOrgFontWeight || 'semibold');
        setStickerShowPosition(settings.stickerShowPosition !== undefined ? settings.stickerShowPosition : true);
        setStickerShowOrganization(settings.stickerShowOrganization !== undefined ? settings.stickerShowOrganization : true);
      } catch (error) {
        console.error('Error loading badge settings:', error);
      }
    }
  }, []);

  // Save settings to localStorage whenever they change
  useEffect(() => {
    const settings = {
      defaultTemplate,
      showQR,
      showLogo,
      stickerSize,
      defaultPrintMode,
      stickerWidth,
      stickerHeight,
      useCustomStickerSize,
      printerType,
      thermalLabelSize,
      stickerNameFontSize,
      stickerPositionFontSize,
      stickerOrgFontSize,
      stickerLineHeight,
      stickerLogoSize,
      stickerShowLogo,
      stickerAutoScaleFont,
      qrPosition,
      stickerNameFontWeight,
      stickerPositionFontWeight,
      stickerOrgFontWeight,
      stickerShowPosition,
      stickerShowOrganization,
    };
    localStorage.setItem('badgeSettings', JSON.stringify(settings));
  }, [defaultTemplate, showQR, showLogo, stickerSize, defaultPrintMode, stickerWidth, stickerHeight, useCustomStickerSize, printerType, thermalLabelSize, stickerNameFontSize, stickerPositionFontSize, stickerOrgFontSize, stickerLineHeight, stickerLogoSize, stickerShowLogo, stickerAutoScaleFont, qrPosition, stickerNameFontWeight, stickerPositionFontWeight, stickerOrgFontWeight, stickerShowPosition, stickerShowOrganization]);

  // Legacy update functions removed - using database-backed versions instead

  const updateStickerWidth = (width) => {
    setStickerWidth(width);
  };

  const updateStickerHeight = (height) => {
    setStickerHeight(height);
  };

  const updateUseCustomStickerSize = (value) => {
    setUseCustomStickerSize(value);
  };

  const updatePrinterType = (value) => {
    setPrinterType(value);
  };

  const updateThermalLabelSize = (value) => {
    setThermalLabelSize(value);
  };

  // Load settings for a specific event
  const loadEventSettings = async (eventId) => {
    if (!eventId || eventId === currentEventId) return;

    setIsLoading(true);
    try {
      const response = await printSettingsAPI.getSettings(eventId);
      const settings = response.data.settings;

      setDefaultTemplate(settings.default_template || 'classic');
      setShowQR(settings.show_qr_code !== undefined ? settings.show_qr_code : true);
      setShowLogo(settings.show_logo_on_badges !== undefined ? settings.show_logo_on_badges : true);
      setStickerSize(settings.sticker_size || 'normal');
      setDefaultPrintMode(settings.default_print_mode || 'sticker');
      setStickerWidth(settings.sticker_width || 224);
      setStickerHeight(settings.sticker_height || 176);
      setUseCustomStickerSize(settings.use_custom_sticker_size || false);
      setPrinterType(settings.printer_type || 'regular');
      setThermalLabelSize(settings.thermal_label_size || '2x1');
      setStickerNameFontSize(settings.sticker_name_font_size || 14);
      setStickerPositionFontSize(settings.sticker_position_font_size || 11);
      setStickerOrgFontSize(settings.sticker_org_font_size || 11);
      setStickerLineHeight(settings.sticker_line_height || 1.2);
      setStickerLogoSize(settings.sticker_logo_size || 40);
      setStickerShowLogo(settings.sticker_show_logo !== undefined ? settings.sticker_show_logo : true);
      setStickerAutoScaleFont(settings.sticker_auto_scale_font !== undefined ? settings.sticker_auto_scale_font : true);
      setQrPosition(settings.qr_position || 'top');
      setStickerNameFontWeight(settings.sticker_name_font_weight || 'bold');
      setStickerPositionFontWeight(settings.sticker_position_font_weight || 'semibold');
      setStickerOrgFontWeight(settings.sticker_org_font_weight || 'semibold');
      setStickerShowPosition(settings.sticker_show_position !== undefined ? settings.sticker_show_position : true);
      setStickerShowOrganization(settings.sticker_show_organization !== undefined ? settings.sticker_show_organization : true);
      setCurrentEventId(eventId);
    } catch (error) {
      console.error('Failed to load event settings:', error);
      // Fall back to localStorage settings if API fails
    } finally {
      setIsLoading(false);
    }
  };

  // Save settings for current event
  const saveEventSettings = async () => {
    if (!currentEventId) return;

    try {
      const settings = {
        default_template: defaultTemplate,
        show_qr_code: showQR,
        show_logo_on_badges: showLogo,
        sticker_size: stickerSize,
        default_print_mode: defaultPrintMode,
        sticker_width: stickerWidth,
        sticker_height: stickerHeight,
        use_custom_sticker_size: useCustomStickerSize,
        printer_type: printerType,
        thermal_label_size: thermalLabelSize,
        sticker_name_font_size: stickerNameFontSize,
        sticker_position_font_size: stickerPositionFontSize,
        sticker_org_font_size: stickerOrgFontSize,
        sticker_line_height: stickerLineHeight,
        sticker_logo_size: stickerLogoSize,
        sticker_show_logo: stickerShowLogo,
        sticker_auto_scale_font: stickerAutoScaleFont,
        qr_position: qrPosition,
        sticker_name_font_weight: stickerNameFontWeight,
        sticker_position_font_weight: stickerPositionFontWeight,
        sticker_org_font_weight: stickerOrgFontWeight,
        sticker_show_position: stickerShowPosition,
        sticker_show_organization: stickerShowOrganization,
      };

      await printSettingsAPI.updateSettings(currentEventId, settings);
    } catch (error) {
      console.error('Failed to save event settings:', error);
    }
  };

  // Enhanced update functions that save to database
  const updateDefaultTemplateWithSave = async (template) => {
    setDefaultTemplate(template);
    if (currentEventId) {
      try {
        await printSettingsAPI.updateSettings(currentEventId, { default_template: template });
      } catch (error) {
        console.error('Failed to save template setting:', error);
      }
    }
  };

  const updateShowQRWithSave = async (show) => {
    setShowQR(show);
    if (currentEventId) {
      try {
        await printSettingsAPI.updateSettings(currentEventId, { show_qr_code: show });
      } catch (error) {
        console.error('Failed to save QR setting:', error);
      }
    }
  };

  const updateShowLogoWithSave = async (show) => {
    setShowLogo(show);
    if (currentEventId) {
      try {
        await printSettingsAPI.updateSettings(currentEventId, { show_logo_on_badges: show });
      } catch (error) {
        console.error('Failed to save logo setting:', error);
      }
    }
  };

  const updateStickerSizeWithSave = async (size) => {
    setStickerSize(size);
    if (currentEventId) {
      try {
        await printSettingsAPI.updateSettings(currentEventId, { sticker_size: size });
      } catch (error) {
        console.error('Failed to save sticker size setting:', error);
      }
    }
  };

  const updateDefaultPrintModeWithSave = async (mode) => {
    setDefaultPrintMode(mode);
    if (currentEventId) {
      try {
        await printSettingsAPI.updateSettings(currentEventId, { default_print_mode: mode });
      } catch (error) {
        console.error('Failed to save print mode setting:', error);
      }
    }
  };

  // Update functions for new sticker customization settings (with database save)
  const updateStickerNameFontSize = async (size) => {
    setStickerNameFontSize(size);
    if (currentEventId) {
      try {
        await printSettingsAPI.updateSettings(currentEventId, { sticker_name_font_size: size });
      } catch (error) {
        console.error('Failed to save name font size setting:', error);
      }
    }
  };

  const updateStickerPositionFontSize = async (size) => {
    setStickerPositionFontSize(size);
    if (currentEventId) {
      try {
        await printSettingsAPI.updateSettings(currentEventId, { sticker_position_font_size: size });
      } catch (error) {
        console.error('Failed to save position font size setting:', error);
      }
    }
  };

  const updateStickerOrgFontSize = async (size) => {
    setStickerOrgFontSize(size);
    if (currentEventId) {
      try {
        await printSettingsAPI.updateSettings(currentEventId, { sticker_org_font_size: size });
      } catch (error) {
        console.error('Failed to save org font size setting:', error);
      }
    }
  };

  const updateStickerLineHeight = async (height) => {
    setStickerLineHeight(height);
    if (currentEventId) {
      try {
        await printSettingsAPI.updateSettings(currentEventId, { sticker_line_height: height });
      } catch (error) {
        console.error('Failed to save line height setting:', error);
      }
    }
  };

  const updateStickerLogoSize = async (size) => {
    setStickerLogoSize(size);
    if (currentEventId) {
      try {
        await printSettingsAPI.updateSettings(currentEventId, { sticker_logo_size: size });
      } catch (error) {
        console.error('Failed to save logo size setting:', error);
      }
    }
  };

  const updateStickerShowLogo = async (show) => {
    setStickerShowLogo(show);
    if (currentEventId) {
      try {
        await printSettingsAPI.updateSettings(currentEventId, { sticker_show_logo: show });
      } catch (error) {
        console.error('Failed to save show logo setting:', error);
      }
    }
  };

  const updateStickerAutoScaleFont = async (auto) => {
    setStickerAutoScaleFont(auto);
    if (currentEventId) {
      try {
        await printSettingsAPI.updateSettings(currentEventId, { sticker_auto_scale_font: auto });
      } catch (error) {
        console.error('Failed to save auto scale font setting:', error);
      }
    }
  };

  const updateQrPosition = async (position) => {
    setQrPosition(position);
    if (currentEventId) {
      try {
        await printSettingsAPI.updateSettings(currentEventId, { qr_position: position });
      } catch (error) {
        console.error('Failed to save QR position setting:', error);
      }
    }
  };

  const updateStickerNameFontWeight = async (weight) => {
    setStickerNameFontWeight(weight);
    if (currentEventId) {
      try {
        await printSettingsAPI.updateSettings(currentEventId, { sticker_name_font_weight: weight });
      } catch (error) {
        console.error('Failed to save name font weight setting:', error);
      }
    }
  };

  const updateStickerPositionFontWeight = async (weight) => {
    setStickerPositionFontWeight(weight);
    if (currentEventId) {
      try {
        await printSettingsAPI.updateSettings(currentEventId, { sticker_position_font_weight: weight });
      } catch (error) {
        console.error('Failed to save position font weight setting:', error);
      }
    }
  };

  const updateStickerOrgFontWeight = async (weight) => {
    setStickerOrgFontWeight(weight);
    if (currentEventId) {
      try {
        await printSettingsAPI.updateSettings(currentEventId, { sticker_org_font_weight: weight });
      } catch (error) {
        console.error('Failed to save organization font weight setting:', error);
      }
    }
  };

  const updateStickerShowPosition = async (show) => {
    setStickerShowPosition(show);
    if (currentEventId) {
      try {
        await printSettingsAPI.updateSettings(currentEventId, { sticker_show_position: show });
      } catch (error) {
        console.error('Failed to save show position setting:', error);
      }
    }
  };

  const updateStickerShowOrganization = async (show) => {
    setStickerShowOrganization(show);
    if (currentEventId) {
      try {
        await printSettingsAPI.updateSettings(currentEventId, { sticker_show_organization: show });
      } catch (error) {
        console.error('Failed to save show organization setting:', error);
      }
    }
  };

  const value = {
    defaultTemplate,
    showQR,
    showLogo,
    stickerSize,
    defaultPrintMode,
    stickerWidth,
    stickerHeight,
    useCustomStickerSize,
    printerType,
    thermalLabelSize,
    currentEventId,
    isLoading,
    // New sticker customization settings
    stickerNameFontSize,
    stickerPositionFontSize,
    stickerOrgFontSize,
    stickerLineHeight,
    stickerLogoSize,
    stickerShowLogo,
    stickerAutoScaleFont,
    qrPosition,
    stickerNameFontWeight,
    stickerPositionFontWeight,
    stickerOrgFontWeight,
    stickerShowPosition,
    stickerShowOrganization,
    // Database-backed functions (these override the legacy ones)
    updateDefaultTemplate: updateDefaultTemplateWithSave,
    updateShowQR: updateShowQRWithSave,
    updateShowLogo: updateShowLogoWithSave,
    updateStickerSize: updateStickerSizeWithSave,
    updateDefaultPrintMode: updateDefaultPrintModeWithSave,
    // Sticker dimension functions
    updateStickerWidth,
    updateStickerHeight,
    updateUseCustomStickerSize,
    updatePrinterType,
    updateThermalLabelSize,
    // New sticker customization update functions
    updateStickerNameFontSize,
    updateStickerPositionFontSize,
    updateStickerOrgFontSize,
    updateStickerLineHeight,
    updateStickerLogoSize,
    updateStickerShowLogo,
    updateStickerAutoScaleFont,
    updateQrPosition,
    updateStickerNameFontWeight,
    updateStickerPositionFontWeight,
    updateStickerOrgFontWeight,
    updateStickerShowPosition,
    updateStickerShowOrganization,
    // Event management functions
    loadEventSettings,
    saveEventSettings,
  };

  return (
    <BadgeSettingsContext.Provider value={value}>
      {children}
    </BadgeSettingsContext.Provider>
  );
};

export default BadgeSettingsContext;
